import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";

function goToDest(nav, loc, u) {
  const dest = loc.state?.from || (u.role === "admin" ? "/admin" : u.role === "designer" ? "/designer" : "/dashboard");
  nav(dest, { replace: true });
}

function PasswordLogin() {
  const { login, completeMfaLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u.mfa_required) {
        setMfaToken(u.mfa_token);
        return;
      }
      toast.success(`Welcome back, ${u.name}!`);
      goToDest(nav, loc, u);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await completeMfaLogin(mfaToken, code.trim());
      toast.success(`Welcome back, ${u.name}!`);
      goToDest(nav, loc, u);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  if (mfaToken) {
    return (
      <form onSubmit={submitMfa} className="space-y-4 mt-6">
        <p className="text-sm text-gray-600">
          Enter the 6-digit code from your authenticator app.{" "}
          <button type="button" onClick={() => setMfaToken(null)} className="text-[#F7941D] font-semibold hover:underline">Back</button>
        </p>
        <div>
          <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Authenticator Code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            required
            maxLength={6}
            autoFocus
            data-testid="login-2fa-code"
            className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 tracking-[0.5em] text-center text-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
          />
        </div>
        <button disabled={submitting} type="submit" data-testid="login-2fa-submit" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Verify &amp; Sign In
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 mt-6">
      <div>
        <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Password</label>
          <Link to="/forgot-password" className="text-xs text-[#F7941D] font-semibold hover:underline">Forgot password?</Link>
        </div>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
      </div>
      <button disabled={submitting} type="submit" data-testid="login-submit" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
      </button>
    </form>
  );
}

function PhoneOtpLogin() {
  const { requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [stage, setStage] = useState("phone"); // phone | otp
  const [purpose, setPurpose] = useState("login"); // login | signup — decided by the first request
  const [submitting, setSubmitting] = useState(false);

  const fullPhone = () => {
    const digits = phone.replace(/\D/g, "").slice(-10);
    return `+91${digits}`;
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setSubmitting(true);
    try {
      // Try login first; if no account exists yet, transparently fall back to signup —
      // the user just sees "enter your phone" either way, no separate signup flow.
      try {
        await requestOtp(fullPhone(), "login");
        setPurpose("login");
      } catch (err) {
        if (err?.response?.status === 404) {
          await requestOtp(fullPhone(), "signup");
          setPurpose("signup");
        } else {
          throw err;
        }
      }
      setStage("otp");
      toast.success("OTP sent via WhatsApp");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send OTP");
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (purpose === "signup" && !name.trim()) { toast.error("Enter your name to finish creating your account"); return; }
    setSubmitting(true);
    try {
      const u = await verifyOtp(fullPhone(), otp.trim(), purpose, purpose === "signup" ? name.trim() : undefined);
      toast.success(purpose === "signup" ? `Welcome, ${u.name}!` : `Welcome back, ${u.name}!`);
      goToDest(nav, loc, u);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invalid OTP");
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "phone") {
    return (
      <form onSubmit={sendOtp} className="space-y-4 mt-6">
        <div>
          <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Mobile Number</label>
          <div className="flex items-center gap-2 mt-1 border border-gray-300 rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-[#F7941D]">
            <span className="text-sm text-gray-500">+91</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit mobile"
              required
              data-testid="login-otp-phone"
              className="flex-1 outline-none"
            />
          </div>
        </div>
        <button disabled={submitting} type="submit" data-testid="login-otp-send" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Send OTP via WhatsApp
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-4 mt-6">
      <p className="text-sm text-gray-600">
        Code sent to <strong>+91 {phone}</strong>.{" "}
        <button type="button" onClick={() => setStage("phone")} className="text-[#F7941D] font-semibold hover:underline">Change number</button>
      </p>
      {purpose === "signup" && (
        <div>
          <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">Your Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="login-otp-name" className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
        </div>
      )}
      <div>
        <label className="text-xs font-semibold text-[#1a3c5e] uppercase tracking-wider">6-Digit OTP</label>
        <input
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          required
          maxLength={6}
          data-testid="login-otp-code"
          className="w-full mt-1 border border-gray-300 rounded-xl px-4 py-3 tracking-[0.5em] text-center text-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
        />
      </div>
      <button disabled={submitting} type="submit" data-testid="login-otp-verify" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition flex items-center justify-center gap-2">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />} {purpose === "signup" ? "Verify & Create Account" : "Verify & Sign In"}
      </button>
    </form>
  );
}

export default function Login() {
  const [mode, setMode] = useState("password"); // password | phone

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-[#FEF3E2]/40">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#1a3c5e]/10 p-8">
        <div className="flex items-center gap-2 mb-1">
          <img
            src="https://housedesignwala.com/api/files/18b1b263-2c1d-4631-8cfa-3c743bdb0809"
            alt="HouseDesignWala"
            className="w-14 h-14 rounded-full object-cover"
          />
          <span className="font-display font-bold text-[#1a3c5e]">HouseDesignWala™</span>
        </div>
        <h1 className="font-display text-2xl font-bold text-[#1a3c5e] mt-3">Welcome back</h1>
        <p className="text-sm text-gray-500 mt-1">Sign in to access your dashboard.</p>

        <div className="flex gap-1.5 mt-5 bg-[#FEF3E2] p-1 rounded-xl">
          <button
            onClick={() => setMode("password")}
            data-testid="login-mode-password"
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition ${mode === "password" ? "bg-white text-[#1a3c5e] shadow-sm" : "text-gray-500"}`}
          >
            Email &amp; Password
          </button>
          <button
            onClick={() => setMode("phone")}
            data-testid="login-mode-phone"
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition inline-flex items-center justify-center gap-1.5 ${mode === "phone" ? "bg-white text-[#1a3c5e] shadow-sm" : "text-gray-500"}`}
          >
            <Phone className="w-3.5 h-3.5" /> Phone (OTP)
          </button>
        </div>

        {mode === "password" ? <PasswordLogin /> : <PhoneOtpLogin />}

        {mode === "password" && (
          <p className="text-sm text-gray-500 mt-5 text-center">
            New here? <Link to="/register" className="text-[#F7941D] font-semibold hover:underline">Create account</Link>
          </p>
        )}
        {mode === "phone" && (
          <p className="text-xs text-gray-400 mt-5 text-center">
            New numbers get a fresh account automatically — no separate signup needed.
          </p>
        )}
        {process.env.NODE_ENV !== "production" && mode === "password" && (
          <div className="mt-5 p-3 rounded-lg bg-[#FEF3E2] text-xs text-[#1a3c5e]/80">
            <strong>Demo accounts (dev only):</strong><br/>
            customer@test.com / customer123<br/>
            designer@test.com / designer123<br/>
            admin@housedesignwala.com / admin123
          </div>
        )}
      </div>
    </div>
  );
}
